import { useState, useRef, useEffect } from "react";
import svgPaths from "./Swatch-Icon.ts";

export function AnimatedSwatchIcon({ size = 100 }: { size?: number }) {
  // Original SVG dimensions from viewBox
  const originalWidth = 400;
  const originalHeight = 400;
  
  // Calculate the height maintaining aspect ratio
  const scaledHeight = size * (originalHeight / originalWidth);
  
  // The anchor point coordinates (center of the black dot)
  const anchorX = 109.5;
  const anchorY = 292.5;
  
  // Animation state
  const [animationInProgress, setAnimationInProgress] = useState(false);
  const [swatch1Rotation, setSwatch1Rotation] = useState(0);
  const [swatch2Rotation, setSwatch2Rotation] = useState(0);
  const [swatch1Transition, setSwatch1Transition] = useState("");
  const [swatch2Transition, setSwatch2Transition] = useState("");
  const animationTimeoutRef = useRef<NodeJS.Timeout[]>([]);
  
  // Responsive bottom margin state
  const [bottomMargin, setBottomMargin] = useState('-4px');
  const [leftMargin, setLeftMargin] = useState('6px');
  const [rightMargin, setRightMargin] = useState('6px');
  
  // Update bottom margin based on screen width
  useEffect(() => {
    const updateMargin = () => {
      setBottomMargin(window.innerWidth <= 720 ? '-2px' : '-4px');
      setLeftMargin(window.innerWidth <= 720 ? '4px' : '6px');
      setRightMargin(window.innerWidth <= 720 ? '6px' : '6px');
    };
    
    // Set initial margin
    updateMargin();
    
    // Add event listener
    window.addEventListener('resize', updateMargin);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateMargin);
  }, []);
  
  // Reset rotation values without triggering animation
  // This is used to prepare for the next animation sequence
  const silentlyResetRotations = () => {
    // Temporarily disable transitions
    setSwatch1Transition("none");
    setSwatch2Transition("none");
    
    // Wait for React to apply the disabled transitions
    setTimeout(() => {
      // Reset rotation values
      setSwatch1Rotation(0);
      setSwatch2Rotation(0);
      
      // Wait for React to apply the rotation reset
      setTimeout(() => {
        // Re-enable transitions for future animations
        setSwatch1Transition("");
        setSwatch2Transition("");
        
        // Allow new animations to start
        setAnimationInProgress(false);
      }, 20);
    }, 20);
  };
  
  // Clean up timeouts when component unmounts
  useEffect(() => {
    return () => {
      animationTimeoutRef.current.forEach(clearTimeout);
    };
  }, []);
  
  // Start the animation sequence on hover
  const startAnimation = () => {
    // Don't restart animation if already running
    if (animationInProgress) return;
    
    // Clear any existing timeouts
    animationTimeoutRef.current.forEach(clearTimeout);
    animationTimeoutRef.current = [];
    
    setAnimationInProgress(true);
    
    // Step 1: Swatch 2 falls flat to 45 degrees
    setSwatch2Transition("transform 0.25s cubic-bezier(0.17, 0.67, 0.83, 0.97)");
    setSwatch2Rotation(45);
    
    // Step 2: After a delay, Swatch 1 falls flat to 90 degrees
    const timeout1 = setTimeout(() => {
      setSwatch1Transition("transform 0.25s cubic-bezier(0.17, 0.67, 0.83, 0.97)");
      setSwatch1Rotation(90);
    }, 250);
    animationTimeoutRef.current.push(timeout1);
    
    // Step 3: After both have fallen, rotate both back to original positions
    const timeout2 = setTimeout(() => {
      // Swatch 2 rotates first
      setSwatch2Transition("transform 0.8s ease-in-out");
      setSwatch2Rotation(360);
      
      // Swatch 1 rotates after a 150ms delay
      const timeout3 = setTimeout(() => {
        setSwatch1Transition("transform 0.8s ease-in-out");
        setSwatch1Rotation(360);
      }, 150);
      animationTimeoutRef.current.push(timeout3);
    }, 500);
    animationTimeoutRef.current.push(timeout2);
    
    // Prepare for the next animation after this one finishes
    // Wait for rotation animations to complete + a small buffer
    // 500ms (fall) + 150ms (delay) + 800ms (rotation) + 100ms (buffer) = 1550ms
    const timeout4 = setTimeout(() => {
      silentlyResetRotations();
    }, 1550);
    animationTimeoutRef.current.push(timeout4);
  };
  
  return (
    <svg
      width={size}
      height={scaledHeight}
      viewBox={`0 0 ${originalWidth} ${originalHeight}`}
      fill="none"
      className="cursor-pointer"
      onMouseEnter={startAnimation}
      style={{ overflow: 'visible', marginBottom: bottomMargin, marginLeft: leftMargin, marginRight: rightMargin }}
    >
      {/* Base layer with white background */}
      <rect fill="white" height={originalHeight} width={originalWidth} />
      
      {/* Swatch 3 (bottom) - NEVER MOVES */}
      <g id="Swatch3">
        <path
          clipRule="evenodd"
          d={svgPaths.p3652e180}
          fill="var(--fill-0, black)"
          fillRule="evenodd"
        />
        <path
          d={svgPaths.p2a6cbf80}
          fill="var(--fill-0, white)"
        />
      </g>
      
      {/* Swatch 2 (middle) */}
      <g 
        id="Swatch2"
        style={{
          transform: `rotate(${swatch2Rotation}deg)`,
          transformOrigin: `${anchorX}px ${anchorY}px`,
          transition: swatch2Transition
        }}
      >
        <path
          clipRule="evenodd"
          d={svgPaths.p35dfe300}
          fill="var(--fill-0, black)"
          fillRule="evenodd"
        />
        <path
          d={svgPaths.p17b22240}
          fill="var(--fill-0, white)"
        />
      </g>
      
      {/* Swatch 1 (top) */}
      <g 
        id="Swatch1"
        style={{
          transform: `rotate(${swatch1Rotation}deg)`,
          transformOrigin: `${anchorX}px ${anchorY}px`,
          transition: swatch1Transition
        }}
      >
        <path
          clipRule="evenodd"
          d={svgPaths.p33ff2300}
          fill="var(--fill-0, black)"
          fillRule="evenodd"
        />
        <path
          clipRule="evenodd"
          d={svgPaths.pde2bb00}
          fill="var(--fill-0, white)"
          fillRule="evenodd"
        />
      </g>
      
      {/* Anchor point (with highest z-index) */}
      <g id="Anchor">
        <path 
          d={svgPaths.p7e0f4f0}
          fill="var(--fill-0, black)" 
        />
      </g>
    </svg>
  );
}